/** @format */

import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { ICollectionsMetrics } from "../../../utility/types/collectTypes";
import { colors } from "../../../utility/types/constant";
import {
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
} from "../styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectionsBarChart — grouped comparison bar chart.
// Shows "This Month" vs "Full Range" for key financial metrics.
// Uses react-apexcharts (already in package.json, no ESM issues).
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    metrics: ICollectionsMetrics | null;
    isLoading: boolean;
}

function fmtFull(n: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(n || 0);
    } catch {
        return `$${(n || 0).toFixed(0)}`;
    }
}

function fmtCompact(n: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            notation: "compact",
            maximumFractionDigits: 1,
        }).format(n || 0);
    } catch {
        return `$${(n || 0).toFixed(0)}`;
    }
}

const CollectionsBarChart: React.FC<Props> = ({ metrics, isLoading }) => {
    const currency = metrics?.currency ?? "USD";

    const monthCollected = metrics?.monthCollected?.amount ?? 0;
    const overdueTotal =
        (metrics?.overdue?.amount ?? metrics?.overdueCurrentMonth?.amount) ?? 0;
    const promises = metrics?.promises?.amount ?? 0;
    const totalCollected = metrics?.collected?.amount ?? 0;
    const totalOutstanding = metrics?.outstanding?.amount ?? 0;
    const total = metrics?.total ?? totalCollected + totalOutstanding;

    // One distinct, semantic color per bucket — kept in sync with the
    // summary pills below so the bar and its pill always match.
    const BUCKET_COLORS = [
        colors.mainSecondary, // Collected  → green
        "#ef4444", // Overdue     → red
        "#f59e0b", // Outstanding → amber
        colors.blueRibbon, // Promised    → blue
    ];

    const chartOptions: ApexOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false },
            fontFamily: "'Montserrat', sans-serif",
            background: "transparent",
            animations: { enabled: true, speed: 500 },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "55%",
                borderRadius: 5,
                borderRadiusApplication: "end" as any,
                // Each category gets its own color from the `colors` array.
                distributed: true,
            },
        },
        dataLabels: { enabled: false },
        colors: BUCKET_COLORS,
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "right",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 600,
            fontSize: "11px",
            labels: { colors: "#6b7280" },
            markers: { size: 7 } as any,
        },
        xaxis: {
            categories: ["Collected", "Overdue", "Outstanding", "Promised"],
            labels: {
                style: {
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 600,
                    fontSize: "11px",
                    colors: "#6b7280",
                },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                formatter: (v: number) => fmtCompact(v, currency),
                style: {
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "10px",
                    colors: "#9ca3af",
                },
            },
        },
        tooltip: {
            theme: "light",
            style: { fontFamily: "'Montserrat', sans-serif", fontSize: "12px" },
            y: { formatter: (v: number) => fmtFull(v, currency) },
        },
        grid: {
            borderColor: "rgba(15,23,42,0.06)",
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        fill: {
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.12,
                opacityFrom: 1,
                opacityTo: 0.85,
            },
        },
    };

    const chartSeries = [
        {
            name: "Amount",
            data: [
                totalCollected,
                overdueTotal,
                totalOutstanding,
                promises,
            ],
        },
    ];

    const PILLS = [
        {
            label: "Collected (range)",
            value: totalCollected,
            hint: `${metrics?.collected?.count ?? 0} invoices`,
            color: colors.mainSecondary,
        },
        {
            label: "This month",
            value: monthCollected,
            hint: `${metrics?.monthCollected?.count ?? 0} invoices`,
            color: "#10b981",
        },
        {
            label: "Outstanding",
            value: totalOutstanding,
            hint: `${metrics?.outstanding?.count ?? 0} open`,
            color: "#f59e0b",
        },
        {
            label: "Promised",
            value: promises,
            hint: `${metrics?.promises?.count ?? 0} pending`,
            color: colors.blueRibbon,
        },
        {
            label: "Total A/R",
            value: total,
            hint: "collected + outstanding",
            color: colors.primary,
        },
    ];

    return (
        <CollectSectionCard>
            <Box sx={{ mb: 2 }}>
                <CollectSectionTitle>Collections Performance</CollectSectionTitle>
                <CollectSectionSubtitle>
                    Collected, overdue, outstanding &amp; promised across the selected range
                </CollectSectionSubtitle>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" py={5}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : !metrics ? (
                <Box py={4} textAlign="center">
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.85rem",
                            color: "#9ca3af",
                        }}
                    >
                        No collections data for this range yet.
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Summary pills */}
                    <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mb: 2.5 }}>
                        {PILLS.map((p) => (
                            <Box
                                key={p.label}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(15,23,42,0.04)",
                                    flex: "1 1 110px",
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "3px",
                                        backgroundColor: p.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.6rem",
                                            fontWeight: 600,
                                            color: "#6b7280",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.06em",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {p.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.9rem",
                                            fontWeight: 700,
                                            color: colors.primary,
                                        }}
                                    >
                                        {fmtCompact(p.value, currency)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.65rem",
                                            color: "#9ca3af",
                                        }}
                                    >
                                        {p.hint}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Bar chart */}
                    <Box sx={{ mx: -0.5 }}>
                        <ReactApexChart
                            type="bar"
                            height={240}
                            options={chartOptions}
                            series={chartSeries}
                        />
                    </Box>
                </>
            )}
        </CollectSectionCard>
    );
};

export default CollectionsBarChart;
